/**
 * Sidenav — vertical navigation list with active accent bar.
 *
 * Design system mapping (mch-sidenav family):
 *   • Root: w-60 py-2
 *   • Framed variant: rounded-md border webex-gray-300 dark: webex-gray-700
 *   • Group label: px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider
 *   • Item: h-9 px-3 mx-2 my-0.5 rounded-md gap-3 text-sm
 *           default text-webex-gray-700 dark: webex-gray-300
 *           hover bg webex-gray-100 dark: webex-gray-850 + text full
 *           active bg webex-gray-200 dark: webex-gray-800 + text full font-medium
 *           active accent: ::before 4px webex-gray-1000 dark: white left bar
 *
 * Composition:
 *   <Sidenav.Root framed>
 *     <Sidenav.Group>Monitoring</Sidenav.Group>
 *     <Sidenav.Item href="/x" active icon={<Icon />}>Workspaces</Sidenav.Item>
 *     <Sidenav.Item href="/y" icon={<Icon />}>Devices</Sidenav.Item>
 *   </Sidenav.Root>
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const sidenavRootVariants = cva("w-60 py-2 bg-white dark:bg-webex-gray-950", {
  variants: {
    framed: {
      true: "rounded-md border border-webex-gray-300 dark:border-webex-gray-700 inline-block align-top",
      false: "",
    },
  },
  defaultVariants: { framed: false },
})

export interface SidenavRootProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof sidenavRootVariants> {}

const Root = React.forwardRef<HTMLElement, SidenavRootProps>(
  ({ className, framed, ...props }, ref) => (
    <nav
      ref={ref as React.Ref<HTMLElement>}
      className={cn(sidenavRootVariants({ framed }), className)}
      {...props}
    />
  ),
)
Root.displayName = "Sidenav.Root"

function Group({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider",
        "text-webex-gray-700 dark:text-webex-gray-300",
        className,
      )}
      {...props}
    />
  )
}

export interface SidenavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean
  icon?: React.ReactNode
  trailingIcon?: React.ReactNode
  /** Render as a different element (e.g. Inertia <Link>). */
  render?: React.ReactElement
}

const itemBase = [
  "relative flex items-center gap-3 h-9 px-3 mx-2 my-0.5 rounded-md",
  "text-sm select-none whitespace-nowrap cursor-pointer no-underline",
  "transition-colors duration-150 motion-reduce:transition-none",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
  "[&>svg]:size-[18px] [&>img]:size-[18px] [&>svg]:shrink-0 [&>img]:shrink-0",
].join(" ")

const itemDefault = [
  "text-webex-gray-700 dark:text-webex-gray-300",
  "hover:bg-webex-gray-100 hover:text-webex-gray-1000",
  "dark:hover:bg-webex-gray-850 dark:hover:text-white",
].join(" ")

const itemActive = [
  "bg-webex-gray-200 text-webex-gray-1000 font-medium",
  "dark:bg-webex-gray-800 dark:text-white",
  "before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0",
  "before:w-1 before:rounded-l-md before:bg-webex-gray-1000 dark:before:bg-white",
].join(" ")

const Item = React.forwardRef<HTMLAnchorElement, SidenavItemProps>(
  ({ className, active, icon, trailingIcon, children, render, ...props }, ref) => {
    const merged = cn(itemBase, active ? itemActive : itemDefault, className)
    const content = (
      <>
        {icon}
        <span className="flex-1 overflow-hidden text-ellipsis">{children}</span>
        {trailingIcon && (
          <span className="shrink-0 opacity-60 [&>svg]:size-3.5 [&>img]:size-3.5">
            {trailingIcon}
          </span>
        )}
      </>
    )
    if (render) {
      return React.cloneElement(
        render,
        {
          className: cn(merged, (render.props as { className?: string }).className),
          "aria-current": active ? "page" : undefined,
          ref,
          ...props,
        } as React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLAnchorElement> },
        content,
      )
    }
    return (
      <a ref={ref} className={merged} aria-current={active ? "page" : undefined} {...props}>
        {content}
      </a>
    )
  },
)
Item.displayName = "Sidenav.Item"

export const Sidenav = { Root, Group, Item }
